/**
 * Payments Migration Page
 *
 * Dedicated page for migrating existing payment mandates from:
 * - GoCardless (SEPA Direct Debit)
 * - Stripe (Card payments)
 *
 * Design: Matches QuotesListResponsive style (Volvo-inspired)
 */

import { useState } from 'react';
import {
  Building2,
  CreditCard,
  RefreshCw,
  HelpCircle,
  Upload,
  Link as LinkIcon,
} from 'lucide-react';
import { MandateMigrationPanel } from './MandateMigrationPanel';
import { StripeMigrationPanel } from './StripeMigrationPanel';

type MigrationTab = 'gocardless' | 'stripe';

export function PaymentsMigrationPage() {
  const [activeTab, setActiveTab] = useState<MigrationTab>('gocardless');

  const migrationMethods = [
    {
      icon: RefreshCw,
      title: 'Synchronisation API',
      description: 'Import automatique depuis votre compte',
    },
    {
      icon: Upload,
      title: 'Import CSV',
      description: 'Import en masse via fichier',
    },
    {
      icon: LinkIcon,
      title: 'Association manuelle',
      description: 'Lier les mandats a vos clients',
    },
  ];

  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-gray-900">
            Migration des mandats
          </h2>
          <p className="text-base text-gray-500 font-light tracking-wide mt-2">
            Importez vos mandats de paiement existants depuis GoCardless ou Stripe
          </p>
        </div>
      </div>

      {/* Method Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {migrationMethods.map((method, index) => {
          const Icon = method.icon;
          return (
            <div
              key={index}
              className="bg-white/40 backdrop-blur-xl rounded-2xl border border-white/40 p-5 flex items-start gap-4"
            >
              <div className="p-3 bg-teal-100 rounded-xl shrink-0">
                <Icon className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{method.title}</h3>
                <p className="text-sm text-gray-500 mt-0.5">{method.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex gap-3">
        <button
          onClick={() => setActiveTab('gocardless')}
          className={`
            flex items-center gap-2.5 px-5 py-3 rounded-2xl font-medium transition-all duration-300
            ${activeTab === 'gocardless'
              ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/25'
              : 'bg-white/40 backdrop-blur-xl text-gray-600 hover:bg-white/60 border border-white/40'
            }
          `}
        >
          <Building2 className="w-5 h-5" />
          GoCardless (SEPA)
        </button>
        <button
          onClick={() => setActiveTab('stripe')}
          className={`
            flex items-center gap-2.5 px-5 py-3 rounded-2xl font-medium transition-all duration-300
            ${activeTab === 'stripe'
              ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/25'
              : 'bg-white/40 backdrop-blur-xl text-gray-600 hover:bg-white/60 border border-white/40'
            }
          `}
        >
          <CreditCard className="w-5 h-5" />
          Stripe (Carte)
        </button>
      </div>

      {/* Main Content Container */}
      <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-xl border border-white/40 overflow-hidden">
        <div className="p-6">
          {activeTab === 'gocardless' && <MandateMigrationPanel />}
          {activeTab === 'stripe' && <StripeMigrationPanel />}
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-white/40 backdrop-blur-xl rounded-3xl border border-white/40 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-xl shrink-0">
            <HelpCircle className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">
              Comment fonctionne la migration ?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-teal-700">1</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Synchronisation API</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Connectez votre compte GoCardless ou Stripe pour importer automatiquement vos mandats existants.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-teal-700">2</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Import CSV</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Importez un fichier CSV avec les IDs de mandats et les emails/IDs clients pour une association en masse.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-teal-700">3</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Association manuelle</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Les mandats non associes automatiquement peuvent etre lies manuellement a vos clients.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentsMigrationPage;
